import { ActionTree } from 'vuex';
import { RootState } from '@/store/types';
import {
  LoginInfo,
  User,
  UserResult,
  UsersState
} from '@/store/modules/users/types';
import {
  addPharmacyAccesses,
  createUser,
  getLoginInfo,
  getPharmacyAccesses,
  getUserById,
  getUsers,
  removePharmacyAccess,
  updatePharmacyAccess,
  updateUser
} from '@/services/axiosService';
import { UsersMutations } from './mutations';

const cleanLocalStorage = () => {
  localStorage.removeItem('users');
};

export const actions: ActionTree<UsersState, RootState> = {
  async showUsers({ commit }, { lastKey, pageSize, pharmacyId }) {
    try {
      const usersResult: readonly UserResult[] | undefined = await getUsers(
        lastKey,
        pageSize,
        pharmacyId
      );

      const users: readonly User[] = (usersResult || []).map(
        (user: UserResult) => ({
          ...user,
          role: user.role.name
        })
      );

      commit(UsersMutations.show_users, {
        users,
        lastKey,
        pageSize
      });
    } catch (error: unknown) {
      cleanLocalStorage();
    }
  },
  async addUser(_, data) {
    try {
      await createUser(data);
    } catch (error: unknown) {
      cleanLocalStorage();
    }
  },
  async getUser({ commit }, id) {
    try {
      const user: UserResult | undefined = await getUserById(id);
      commit(UsersMutations.get_user, { user });
    } catch (error: unknown) {
      cleanLocalStorage();
    }
  },
  async updateUser({ commit }, { data, userId }) {
    try {
      const payload = {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        role: data.role,
        pharmacyId: data.pharmacyId,
        pharmacistPin: data.pharmacistPin,
        webToolAccess: data.webToolAccess,
        boxAccess: data.boxAccess
      };

      await updateUser(payload, userId);
      commit(UsersMutations.update_user, { payload });
    } catch (error: unknown) {
      cleanLocalStorage();
    }
  },
  async getPharmacyAccesses({ commit }) {
    try {
      const pharmacyAccesses = await getPharmacyAccesses();
      commit(UsersMutations.get_pharmacyAccesses, { pharmacyAccesses });
    } catch (error: unknown) {
      throw new Error('user_is_not_found');
    }
  },
  async updatePharmacyAccess({ commit }, data) {
    try {
      await updatePharmacyAccess(data);
      commit(UsersMutations.update_pharmacy_access, { data });
    } catch (error: unknown) {
      cleanLocalStorage();
    }
  },
  async addPharmacyAccesses(_, { ...data }) {
    try {
      const payload = {
        pharmacyId: data.pharmacyId,
        email: data.email,
        webToolAccess: data.webToolAccess,
        boxAccess: data.boxAccess,
        roleName: data.roleName
      };

      await addPharmacyAccesses(payload);
    } catch (error: unknown) {
      cleanLocalStorage();
    }
  },
  async removePharmacyAccess(_, userId) {
    try {
      await removePharmacyAccess(userId);
    } catch (error: unknown) {
      cleanLocalStorage();
    }
  },
  async getLoginInfo({ commit }, { pharmacyId, limit, lastTimestamp }) {
    try {
      const loginInfo: LoginInfo[] | undefined = await getLoginInfo(
        pharmacyId,
        limit,
        lastTimestamp
      );

      commit(UsersMutations.get_login_info, { loginInfo, lastTimestamp });
    } catch (error: unknown) {
      cleanLocalStorage();
    }
  },
  clearUsers({ commit }) {
    commit(UsersMutations.clear);
  },
  clearUser({ commit }) {
    commit(UsersMutations.clear_user);
  }
};
